import api from './client'

export interface User {
  user_id: string
  email: string | null
  full_name: string | null
  phone: string | null
  roles: string[]
  tenant_id: string
  active: boolean
  is_placeholder: boolean
  activated_at: string | null
  // docs/plan-user-blocking.md / security-hardening-1.md §3b. Distinct
  // from `active` above: blocking is reversible and still occupies a
  // seat; is_director marks the one immutable account per tenant
  // (I-SOFT sets/clears it, never a tenant admin).
  is_blocked: boolean
  is_director: boolean
  notify_scada: boolean
  google_account: string | null
  ios_account: string | null
  // Optional (not just nullable) per isoft-admin's own handoff wording --
  // defensive in case some response path doesn't populate them at all, not
  // just leaves them null. Set together, never partially, when present;
  // null/null/null is the normal case. Cleared back to all-null
  // automatically server-side when the admin uses the existing "resend
  // invite" action, so a shown badge isn't permanent.
  email_bounce_status?: 'bounced' | 'complained' | null
  email_bounce_type?: string | null // SES's own bounceType/complaintFeedbackType, open-ended
  email_bounce_at?: string | null
}

export interface SeatsInfo {
  used: number
  seats: number
  expires_at: string | null
}

export const usersApi = {
  list: () =>
    api.get<User[]>('/users'),

  getSeats: () =>
    api.get<SeatsInfo>('/users/seats'),

  invite: (data: { email: string; full_name?: string; roles: string[] }) =>
    api.post<User>('/users', data),

  resendInvite: (userId: string) =>
    api.post(`/users/${userId}/resend-invite`),

  assignRoles: (userId: string, roles: string[]) =>
    api.put(`/users/${userId}/roles`, { roles }),

  setActive: (userId: string, active: boolean) =>
    api.put(`/users/${userId}/active`, null, { params: { active } }),

  block: (userId: string) =>
    api.put(`/users/${userId}/block`),

  unblock: (userId: string) =>
    api.put(`/users/${userId}/unblock`),

  deleteUser: (userId: string) =>
    api.delete(`/users/${userId}`),

  setNotifyScada: (userId: string, notify: boolean) =>
    api.put(`/users/${userId}/notify-scada`, null, { params: { notify_scada: notify } }),

  updateProfile: (userId: string, body: { google_account?: string; ios_account?: string }) =>
    api.patch<User>(`/users/${userId}/profile`, body),
}

// modules: null = Core (always available); string[] = requires one of those modules
export const ALL_ROLES = [
  { value: 'company_admin',  label: 'Admin',               modules: null },
  { value: 'executive',      label: 'Direktor',             modules: null },
  { value: 'analyst',        label: 'Analitičar',           modules: null },
  { value: 'dispatcher',     label: 'Dispečer',             modules: ['meter_reading', 'repair'] },
  { value: 'meter_reader',   label: 'Inkasant',             modules: ['meter_reading'] },
  { value: 'repair_crew',    label: 'Ekipa za popravke',    modules: ['repair'] },
  { value: 'scada_operator', label: 'SCADA operater',       modules: ['scada'] },
  { value: 'scada_repair',   label: 'SCADA serviser',       modules: ['scada'] },
]

/** Returns roles assignable in a tenant given its active modules. */
export function rolesForModules(tenantModules: string[]) {
  return ALL_ROLES.filter(
    (r) => r.modules === null || r.modules.some((m) => tenantModules.includes(m))
  )
}

// docs/plan-security-hardening-1.md §3b: the director is I-SOFT's to mark,
// never a tenant admin's -- 'executive' ("Direktor") is a real role that
// happens to be part of how a director account is provisioned, so a tenant
// admin granting it to themselves or anyone else would look exactly like
// self-appointing as director even though it's not the same thing as the
// is_director flag. Only I-SOFT's panels (AdminTenantDetail's
// ProvisionModal, OperatorPage) use rolesForModules() directly and keep
// offering it; UsersPage's tenant-admin-facing invite/role pickers use this
// instead.
export function tenantAssignableRoles(tenantModules: string[]) {
  return rolesForModules(tenantModules).filter((r) => r.value !== 'executive')
}
